import generateCdnUrlWithParams from '@/core/responsive/image/helpers/generate-cdn-url-including-params';
import { IExtendedImageOptions } from '@/core/responsive/image/image.types';

function generateSrc(src: string, options: Partial<IExtendedImageOptions>): string {
    const renderedImageHeight = heightFromWidth(options.width as number, options.aspectRatio ?? 1);
    const finalOptions: Partial<IExtendedImageOptions> = {
        ...options,
        format: 'auto',
        gravity: convertFocusPointToGravity(options, renderedImageHeight),
        height: renderedImageHeight,
        quality: '81',
        trim: null, // convertFocusPointsToTrim(options),
    };

    delete finalOptions.aspectRatio;
    delete finalOptions.focusPoints;
    delete finalOptions.focus;
    delete finalOptions.zoom;
    delete finalOptions.isProductImage;
    delete finalOptions.originalHeight;
    delete finalOptions.originalWidth;

    return generateCdnUrlWithParams(src, finalOptions, options.isProductImage);
}

function heightFromWidth(width: number, aspectRatio: number): number {
    return Math.round(width / aspectRatio);
}

function convertFocusPointToGravity(options: Partial<IExtendedImageOptions>, renderedImageHeight: number): string | null {
    const hasZoomOrFocusEnabled = options.zoom || options.focus;
    if (!hasZoomOrFocusEnabled || !options.focusPoints || !options.originalWidth || !options.originalHeight) {
        return null;
    }

    const parsedFocusPoints = parseFocusPointsToXY(options.focusPoints);

    const renderedImageWidth = parseInt(options.width as any);
    const scaleFactor = !isNaN(renderedImageWidth) ? renderedImageWidth / options.originalWidth : null;

    const gravityX = getGravityPoint(options.originalWidth, parsedFocusPoints.x1, parsedFocusPoints.x2);
    const gravityY = getGravityPoint(options.originalHeight, parsedFocusPoints.y1, parsedFocusPoints.y2, scaleFactor, renderedImageHeight);

    return `${gravityX.toFixed(2)}x${gravityY.toFixed(2)}`;
}

// function convertFocusPointsToTrim(options: Partial<IExtendedImageOptions>): string | null {
//     if (!options.zoom || !options.focusPoints || !options.originalWidth || !options.originalHeight) {
//         return null;
//     }
//
//     const parsedFocusPoints = parseFocusPointsToXY(options.focusPoints);
//
//     const top = parsedFocusPoints.y1;
//     const right = Math.max(options.originalWidth - parsedFocusPoints.x2, 0);
//     const bottom = Math.max(options.originalHeight - parsedFocusPoints.y2, 0);
//     const left = parsedFocusPoints.x1;
//
//     const trimBoxWidth = parsedFocusPoints.x2 - parsedFocusPoints.x1;
//     const trimBoxHeight = parsedFocusPoints.y2 - parsedFocusPoints.y1;
//     const trimBoxAspectRatio = trimBoxWidth / trimBoxHeight;
//     const trimBoxIsLandscapeMode = trimBoxAspectRatio > 1;
//
//     if (trimBoxIsLandscapeMode) {
//         return `${0};${right};${0};${left}`;
//     }
//     return `${top};${0};${bottom};${0}`;
// }

/**
* Calculates the gravity point for a given dimension. The gravity point is the point in the image that should be centered in the rendered image.
* @desc Note that the gravity point needs to be calculated differently for the x and y values. - Hence the extra parameters.
**/
function getGravityPoint(dimension: number, minPoint: number, maxPoint: number, scaleFactor: number | null = null, renderedDimension: number | null = null) {
    const maxPointCorrected = Math.min(maxPoint, dimension);
    const minPointCorrected = Math.max(minPoint, 0);

    if (renderedDimension === null || scaleFactor === null) {
        const focalPoint = (maxPointCorrected + minPointCorrected) / 2;
        return focalPoint / dimension;
    }

    // Calculating the Y dimension is a bit more complicated. We need to take into account the rendered image height.
    const focalPointForDimension = (minPointCorrected + maxPointCorrected) * scaleFactor / 2;

    if (focalPointForDimension < renderedDimension / 2) {
        return 0;
    }

    const scaledImageDimension = dimension * scaleFactor;
    const gravityWindow = scaledImageDimension - renderedDimension;
    const focalPointFromBottom = scaledImageDimension - (renderedDimension / 2);
    if (focalPointForDimension > focalPointFromBottom) {
        return 1;
    }

    const fromBottom = focalPointFromBottom - focalPointForDimension;
    const gravityFocal = gravityWindow - fromBottom;
    return gravityFocal / gravityWindow;
}

function parseFocusPointsToXY(focusPoints: string): { x1: number, x2: number, y1: number, y2: number } {
    const focalToArr = focusPoints.split(',');
    const x1 = parseInt(focalToArr[0], 10);
    const x2 = parseInt(focalToArr[2], 10);
    const y1 = parseInt(focalToArr[1], 10);
    const y2 = parseInt(focalToArr[3], 10);

    return {
        x1,
        x2,
        y1,
        y2,
    };
}

export default generateSrc;
